/**
 * Event-Registry für datalayer-Event-Handler
 *
 * Ermöglicht Services, Event-Handler zu registrieren, die aufgerufen werden,
 * wenn entsprechende Events im DataLayer erscheinen.
 *
 * Buffert Events, die vor Service-Registrierung gefeuert wurden, sodass
 * spät geladene Services (z.B. nach Consent) keine Events verpassen.
 */

const eventHandlers = new Map();
const eventBuffer = [];
const MAX_BUFFER_SIZE = 50; // Verhindert Memory-Leak bei vielen Events
const MAX_EVENT_AGE = 60000; // Events älter als 1 Minute werden verworfen
// Blacklist: Diese Events werden NICHT gebuffert
const NON_BUFFERED_EVENTS = ['como_update'];
// Aktuell in Verarbeitung befindliche Events (für nested Handler-Registrierung)
let currentlyProcessingEvent = null;

/**
 * Registriert einen Event-Handler für ein bestimmtes Event
 * @param {string} eventName - Name des Events (z.B. 'login', 'add_to_cart')
 * @param {Function} handler - Handler-Funktion, die aufgerufen wird
 * @param {string} serviceId - Eindeutige ID des Services (z.B. 'braze', 'contentsquare')
 */
export function registerEventHandler(eventName, handler, serviceId) {
	if (!eventHandlers.has(eventName)) {
		eventHandlers.set(eventName, []);
	}

	const handlers = eventHandlers.get(eventName);
	handlers.push({
		serviceId,
		handler
	});

	// Replay buffered events (außer für Blacklist-Events)
	const isNonBufferedEvent = NON_BUFFERED_EVENTS.includes(eventName);
	if (!isNonBufferedEvent) {
		replayBufferedEvents(eventName, handler, serviceId);

		// WICHTIG: Wenn gerade ein Event verarbeitet wird UND
		// der neue Handler für DASSELBE Event registriert wird,
		// rufe den Handler sofort mit dem aktuellen Event auf
		// (nur wenn der Service das Event noch nicht per Replay bekommen hat)
		if (currentlyProcessingEvent && currentlyProcessingEvent.eventName === eventName) {
			const { bufferedEvent } = currentlyProcessingEvent;

			// Nur ausführen, wenn Service das Event noch nicht bekommen hat
			if (!bufferedEvent || !bufferedEvent.deliveredToServices.has(serviceId)) {
				try {
					handler(currentlyProcessingEvent.model, currentlyProcessingEvent.message);
					// Markiere als zugestellt im Buffer
					if (bufferedEvent) {
						bufferedEvent.deliveredToServices.add(serviceId);
					}
				} catch (error) {
					// Fehler abfangen, damit andere Handler nicht betroffen sind
				}
			}
		}
	}
}

/**
 * Entfernt alle Event-Handler eines bestimmten Services
 * @param {string} serviceId - ID des Services
 */
export function unregisterService(serviceId) {
	for (const [eventName, handlers] of eventHandlers.entries()) {
		const filtered = handlers.filter(h => h.serviceId !== serviceId);
		if (filtered.length === 0) {
			eventHandlers.delete(eventName);
		} else {
			eventHandlers.set(eventName, filtered);
		}
	}
}

/**
 * Führt alle registrierten Handler für ein bestimmtes Event aus
 * @param {string} eventName - Name des Events
 * @param {Object} model - DataLayer-Model
 * @param {Object} message - Event-Message
 */
export function triggerEvent(eventName, model, message) {
	const handlers = eventHandlers.get(eventName);
	const isNonBufferedEvent = NON_BUFFERED_EVENTS.includes(eventName);
	const timestamp = Date.now();

	// WICHTIG: Events IMMER buffern (außer Blacklist), auch wenn Handler existieren
	// Dies ermöglicht es Services, die sich später registrieren, die Events zu bekommen
	let bufferedEvent = null;
	if (!isNonBufferedEvent) {
		bufferedEvent = bufferEventWithTimestamp(eventName, model, message, timestamp);
	}

	// Wenn keine Handler registriert sind, nur buffern und fertig
	if (!handlers || handlers.length === 0) {
		return;
	}

	// Event als "aktuell in Verarbeitung" markieren
	// Dies ermöglicht es, dass während der Verarbeitung registrierte Handler
	// das aktuelle Event sofort erhalten
	const previousEvent = currentlyProcessingEvent;
	currentlyProcessingEvent = {
		eventName,
		model: { ...model },
		message: { ...message },
		timestamp,
		bufferedEvent // Referenz zum gepufferten Event
	};

	try {
		handlers.forEach(({ serviceId, handler }) => {
			try {
				handler(model, message);
			} catch (error) {
			}
		});
	} finally {
		// Nach der Verarbeitung wiederherstellen
		currentlyProcessingEvent = previousEvent;
	}
}

/**
 * Gibt alle registrierten Event-Handler zurück (für Tests/Debugging)
 * @returns {Map}
 */
export function getRegisteredHandlers() {
	return new Map(eventHandlers);
}

/**
 * Löscht alle registrierten Event-Handler (für Tests)
 */
export function clearAllHandlers() {
	eventHandlers.clear();
}

/**
 * Buffert ein Event für spätere Verarbeitung
 * @private
 * @param {string} eventName - Name des Events
 * @param {Object} model - DataLayer-Model
 * @param {Object} message - Event-Message
 * @param {number} timestamp - Optional: Timestamp des Events (default: Date.now())
 * @returns {Object} Das gepufferte Event-Objekt
 */
function bufferEventWithTimestamp(eventName, model, message, timestamp = Date.now()) {
	// Shallow copy um Referenz-Probleme zu vermeiden
	const bufferedEvent = {
		eventName,
		model: { ...model },
		message: { ...message },
		timestamp,
		// Tracke welche Services dieses Event bereits erhalten haben
		deliveredToServices: new Set()
	};

	// Wenn gerade Handler ausgeführt werden, markiere diese Services als "bereits erhalten"
	const handlers = eventHandlers.get(eventName);
	if (handlers && handlers.length > 0) {
		handlers.forEach(({ serviceId }) => {
			bufferedEvent.deliveredToServices.add(serviceId);
		});
	}

	eventBuffer.push(bufferedEvent);

	// Memory-Schutz: Alte Events entfernen
	if (eventBuffer.length > MAX_BUFFER_SIZE) {
		const removed = eventBuffer.shift();
	}

	// Alte Events regelmäßig aufräumen
	cleanupOldBufferedEvents();

	return bufferedEvent;
}

/**
 * Spielt gepufferte Events für einen neu registrierten Handler ab
 * @private
 * @param {string} eventName - Name des Events
 * @param {Function} handler - Handler-Funktion
 * @param {string} serviceId - Service-ID für Logging
 */
function replayBufferedEvents(eventName, handler, serviceId) {
	const relevantEvents = eventBuffer.filter(e => e.eventName === eventName);

	if (relevantEvents.length === 0) {
		return;
	}

	relevantEvents.forEach((bufferedEvent) => {
		const { model, message, timestamp, deliveredToServices } = bufferedEvent;

		// WICHTIG: Nur replayed wenn dieser Service das Event noch NICHT erhalten hat
		if (deliveredToServices.has(serviceId)) {
			return; // Skip - dieser Service hat das Event bereits
		}

		const age = Date.now() - timestamp;

		try {
			handler(model, message);
			// Markiere Service als "Event erhalten"
			deliveredToServices.add(serviceId);
		} catch (error) {
		}
	});
}

/**
 * Entfernt Events aus dem Buffer, die älter als MAX_EVENT_AGE sind
 * @private
 */
function cleanupOldBufferedEvents() {
	const now = Date.now();
	const before = eventBuffer.length;

	// Filter alte Events raus
	let i = 0;
	while (i < eventBuffer.length) {
		if (now - eventBuffer[i].timestamp > MAX_EVENT_AGE) {
			const removed = eventBuffer.splice(i, 1)[0];
		} else {
			i++;
		}
	}

	if (before !== eventBuffer.length) {
	}
}

/**
 * Gibt den aktuellen Event-Buffer zurück (für Tests/Debugging)
 * @returns {Array}
 */
export function getEventBuffer() {
	return [...eventBuffer];
}

/**
 * Löscht alle gepufferten Events (für Tests)
 */
export function clearEventBuffer() {
	eventBuffer.length = 0;
}
